/**
 * Public QA Resolution Landing Page (no login required).
 *
 * Reached from the "Mark as resolved" CTA in the low-rating alert email.
 * Token `?t=...` carries HMAC-signed claim {cid, exp}. Backend re-verifies
 * on both meta-fetch and submit, so this page is harmless to share —
 * without the token the metadata + resolve endpoints return 403.
 *
 * Flow: load metadata → display rating context + form → POST → success
 * confirmation. Idempotent: if the chat is already resolved, the page
 * surfaces the prior resolution instead of letting the user re-submit.
 */
import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, CheckCircle2, ShieldAlert, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

function StarRow({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`w-4 h-4 ${n <= rating ? "text-amber-400 fill-amber-400" : "text-slate-300"}`}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

export default function QaResolvePage() {
  const { cid } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";

  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [action, setAction] = useState("");
  const [notes, setNotes] = useState("");
  const [resolver, setResolver] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token || !cid) {
        setError("Missing or invalid resolution link.");
        setLoading(false);
        return;
      }
      try {
        const { data } = await API.get(`/chat/ratings/${cid}/resolve-meta?t=${encodeURIComponent(token)}`);
        if (cancelled) return;
        setMeta(data);
        if (data.resolution_status === "resolved") {
          // Idempotent landing — show the prior resolution instead of the form.
          setDone({
            resolved_at: data.resolved_at,
            resolved_by: data.resolved_by,
            action: data.resolution_action,
            notes: data.resolution_notes,
          });
        }
      } catch (e) {
        setError(e.response?.data?.detail || "Could not load this chat. The link may have expired.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cid, token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!action) {
      toast.error("Pick a resolution action");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await API.post(`/chat/ratings/${cid}/resolve`, {
        token,
        action,
        notes,
        resolver_name: resolver,
      });
      setDone({
        resolved_at: data.resolved_at,
        resolved_by: data.resolved_by,
        action,
        notes,
      });
      toast.success("Marked as resolved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-amber-50 py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-center mb-6">
          <Link to="/" className="flex items-center gap-2 text-purple-700 hover:text-purple-900">
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>GLPFlo</span>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">QA Review</span>
          </Link>
        </div>

        {loading && (
          <div className="text-center py-16 text-slate-400" data-testid="qa-resolve-loading">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            <p className="text-sm">Loading resolution context…</p>
          </div>
        )}

        {!loading && error && (
          <Card className="border-red-200">
            <CardContent className="p-6 text-center" data-testid="qa-resolve-error">
              <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <h2 className="font-semibold text-slate-900 mb-1">Link unavailable</h2>
              <p className="text-sm text-slate-600">{error}</p>
              <Link to="/" className="inline-block mt-4 text-sm font-semibold text-purple-700 hover:text-purple-900">
                Return to GLPFlo →
              </Link>
            </CardContent>
          </Card>
        )}

        {!loading && !error && meta && (
          <>
            <Card className="mb-4 border-purple-200">
              <CardContent className="p-5" data-testid="qa-resolve-context">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-600 mb-2">Chat being reviewed</p>
                <div className="flex items-center gap-3 mb-2">
                  <StarRow rating={meta.rating || 0} />
                  <span className="text-sm font-semibold text-slate-900">{meta.patient_name || "Patient"}</span>
                  <span className="text-[11px] text-slate-400">{meta.category}</span>
                </div>
                {meta.rating_comment && (
                  <blockquote className="border-l-3 border-l-purple-300 pl-3 text-sm italic text-slate-700 my-3">
                    &quot;{meta.rating_comment}&quot;
                  </blockquote>
                )}
                <p className="text-[11px] text-slate-400">
                  Closed by {meta.closed_by || "—"}
                  {meta.rating_submitted_at && ` · Rated ${new Date(meta.rating_submitted_at).toLocaleString()}`}
                </p>
              </CardContent>
            </Card>

            {done ? (
              <Card className="border-emerald-200 bg-emerald-50/40">
                <CardContent className="p-6 text-center" data-testid="qa-resolve-done">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h2 className="font-bold text-slate-900 mb-1">Marked as resolved</h2>
                  <p className="text-sm text-slate-600 mb-3">
                    Logged at {done.resolved_at && new Date(done.resolved_at).toLocaleString()}
                    {done.resolved_by && ` by ${done.resolved_by}`}.
                  </p>
                  <div className="bg-white border border-slate-200 rounded-lg p-3 text-left text-xs text-slate-600 mb-3">
                    <p><span className="font-semibold text-slate-900">Action:</span> {(meta.actions && meta.actions[done.action]) || done.action}</p>
                    {done.notes && <p className="mt-1"><span className="font-semibold text-slate-900">Notes:</span> {done.notes}</p>}
                  </div>
                  <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-purple-700 hover:text-purple-900">
                    Done <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-5">
                  <form onSubmit={submit} className="space-y-4" data-testid="qa-resolve-form">
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                        What did you do?
                      </label>
                      <div className="space-y-1.5">
                        {Object.entries(meta.actions || {}).map(([key, label]) => (
                          <label
                            key={key}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${action === key ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:bg-slate-50"}`}
                            data-testid={`qa-resolve-action-${key}`}
                          >
                            <input
                              type="radio"
                              name="action"
                              value={key}
                              checked={action === key}
                              onChange={() => setAction(key)}
                              className="accent-purple-600"
                            />
                            <span className="text-sm text-slate-700">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                        Notes (optional)
                      </label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        maxLength={500}
                        placeholder="What happened? Any follow-up planned?"
                        className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                        data-testid="qa-resolve-notes"
                      />
                      <p className="text-[10px] text-slate-400 text-right mt-0.5">{notes.length}/500</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                        Your name (for the audit log)
                      </label>
                      <input
                        type="text"
                        value={resolver}
                        onChange={(e) => setResolver(e.target.value)}
                        maxLength={80}
                        placeholder="e.g. Jordan Lee"
                        className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                        data-testid="qa-resolve-resolver"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={submitting || !action}
                      className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm"
                      data-testid="qa-resolve-submit"
                    >
                      {submitting ? "Saving…" : "Mark as resolved"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
